import React from "react";
import { useStore } from "zustand";
import store from "../../../state/store";
import { twClassNames } from "../helpers/twClassNames";
import { TooltipContainer } from "./Tooltip.jsx";

export function Avatar({
    photoURL,
    displayName,
    tooltipContent,
    shouldShowTooltip,
    photoClassName,
    className,
    size
}) {
    return (
        <TooltipContainer
            disabled={!shouldShowTooltip}
            type="titleOnly"
            title={tooltipContent}
            placement="bottom"
            customButton={
                <div className={twClassNames("avatarCircle relative", size, className)}>
                    {photoURL ? (
                        <img
                            src={photoURL}
                            alt={displayName}
                            className={twClassNames(
                                "h-full w-full rounded-full object-cover",
                                photoClassName
                            )}
                        />
                    ) : (
                        <p>{displayName?.[0] || "?"}</p>
                    )}
                </div>
            }
        />
    );
}

export type AvatarUser = {
    id?: string;
    uid?: string;
    displayName?: string;
    email?: string;
    photoURL?: string;
};

type UserAvatarProps = {
    user: AvatarUser;
    showName?: boolean;
    showAvatar?: boolean;
    showPhoto?: boolean;
    showEmail?: boolean;
    showTooltip?: boolean;
    tooltipDisplayName?: boolean;
    className?: string;
    avatarClassName?: string;
    avatarSize?: "small" | "medium" | "large";
    textClassName?: string;
    displayNameClassName?: string;
    displayEmailClassName?: string;
    photoClassName?: string;
    showPending?: boolean;
};

export function UserAvatar({
    user,
    showName = false,
    showAvatar = false,
    showPhoto = true,
    showEmail = false,
    showTooltip = true,
    tooltipDisplayName = false,
    className = "",
    avatarClassName = "",
    avatarSize = "small",
    textClassName = "",
    displayNameClassName = "",
    displayEmailClassName = "",
    photoClassName = "",
    showPending = false
}: UserAvatarProps) {
    const userId = user?.uid || user?.id || "";
    const workspaceMember = useStore(store, (state) =>
        state?.currentWorkspaceId
            ? state.workspacesById[state.currentWorkspaceId]?.members?.[userId]
            : null
    );
    const displayName = user.displayName || workspaceMember?.displayName;
    const displayNameOrQuestionMark = displayName || "?";
    const email = user.email || workspaceMember?.email;
    const displayNameOrEmail = displayName || email;
    const photoURL = user.photoURL || workspaceMember?.photoURL;
    const tooltipContent = tooltipDisplayName
        ? displayNameOrQuestionMark || email
        : email || displayNameOrQuestionMark;
    const shouldShowTooltip = showTooltip && !showName && !showEmail;

    return (
        <div className={twClassNames("UserAvatar cursor-default", className)}>
            {showAvatar ? (
                <Avatar
                    photoURL={showPhoto ? photoURL : undefined}
                    displayName={displayNameOrEmail}
                    tooltipContent={tooltipContent}
                    shouldShowTooltip={shouldShowTooltip}
                    className={avatarClassName}
                    photoClassName={photoClassName}
                    size={avatarSize}
                />
            ) : (
                ""
            )}
            {(showName || showEmail) && (
                <div className="userInfo">
                    {showName && (
                        <div
                            className={twClassNames(
                                "displayName",
                                textClassName,
                                displayNameClassName
                            )}
                        >
                            {displayNameOrQuestionMark}
                        </div>
                    )}
                    {showEmail && (
                        <div
                            className={twClassNames(
                                "displayEmail",
                                textClassName,
                                displayEmailClassName
                            )}
                        >
                            {email}
                        </div>
                    )}
                </div>
            )}
            {showPending && <div className="chip">Invited</div>}
        </div>
    );
}

type UserCountProps = {
    count: number;
    avatarSize?: "small" | "medium" | "large";
    className?: string;
};

export function UserCount({ count, avatarSize = "small", className = "" }: UserCountProps) {
    return (
        <div className="UserAvatar">
            <div className={twClassNames("avatarCircle cursor-default", avatarSize, className)}>
                +{count}
            </div>
        </div>
    );
}
